import type { Action } from 'redux';

import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError } from '/index';

export interface InitiativeEstimateState {
  tonnageReducedTo: number | null;
  tonnageReducedPerc: number | null;
  tonnageReducedBy: number | null;
  isTip: boolean | null;
  sourceTonnage: number | null;
  awaitingCustomCreation: boolean;
  fetching: boolean;
  fetched: boolean;
  error: NormalisedError | null;
}

interface BasketInitiativeCreateCustomStartAction extends Action {
  type: 'BASKET_INITIATIVE_CREATE_CUSTOM_START';
}

interface BasketInitiativeCreateCustomSaveDetectedAction extends Action {
  type: 'BASKET_INITIATIVE_CREATE_CUSTOM_SAVE_DETECTED';
}

interface BasketInitiativeCreateCustomRejectedAction extends Action {
  type: 'BASKET_INITIATIVE_CREATE_CUSTOM_REJECTED';
  error: NormaliseErrorInput;
}

interface SetEstimateReductionStartAction extends Action {
  type: 'SET_ESTIMATE_REDUCTION_START';
}

interface SetEstimateReductionResetAction extends Action {
  type: 'SET_ESTIMATE_REDUCTION_RESET';
}

interface SetEstimateReductionFulfilledAction extends Action {
  type: 'SET_ESTIMATE_REDUCTION_FULFILLED';
  tonnageReducedTo: number | null;
  tonnageReducedPerc: number | null;
  tonnageReducedBy: number | null;
  isTip: boolean | null;
  sourceTonnage: number | null;
}

interface SetEstimateReductionRejectedAction extends Action {
  type: 'SET_ESTIMATE_REDUCTION_REJECTED';
  error: NormaliseErrorInput;
}

type InitiativeEstimateAction =
  | BasketInitiativeCreateCustomStartAction
  | BasketInitiativeCreateCustomSaveDetectedAction
  | BasketInitiativeCreateCustomRejectedAction
  | SetEstimateReductionStartAction
  | SetEstimateReductionResetAction
  | SetEstimateReductionFulfilledAction
  | SetEstimateReductionRejectedAction;

const defaultState: InitiativeEstimateState = {
  tonnageReducedTo: null,
  tonnageReducedPerc: null,
  tonnageReducedBy: null,
  isTip: null,
  sourceTonnage: null,
  awaitingCustomCreation: false,
  fetching: false,
  fetched: false,
  error: null,
};

function reducer(
  state: InitiativeEstimateState = defaultState,
  action: InitiativeEstimateAction,
): InitiativeEstimateState {
  switch (action.type) {
    case 'BASKET_INITIATIVE_CREATE_CUSTOM_START': {
      return {
        ...state,
        awaitingCustomCreation: true,
      };
    }
    case 'BASKET_INITIATIVE_CREATE_CUSTOM_SAVE_DETECTED': {
      return {
        ...state,
        awaitingCustomCreation: false,
      };
    }
    case 'BASKET_INITIATIVE_CREATE_CUSTOM_REJECTED': {
      const { error } = action;
      return {
        ...state,
        awaitingCustomCreation: false,
        error: normaliseError(error),
        fetching: false,
        fetched: true,
      };
    }
    case 'SET_ESTIMATE_REDUCTION_START': {
      return {
        ...state,
        fetching: true,
        fetched: false,
      };
    }
    case 'SET_ESTIMATE_REDUCTION_RESET': {
      return {
        ...state,
        ...defaultState,
      };
    }
    case 'SET_ESTIMATE_REDUCTION_FULFILLED': {
      const { tonnageReducedTo, tonnageReducedPerc, tonnageReducedBy, isTip, sourceTonnage } = action;
      return {
        ...state,
        error: null,
        fetching: false,
        fetched: true,
        tonnageReducedTo,
        sourceTonnage,
        tonnageReducedPerc,
        tonnageReducedBy,
        isTip,
      };
    }
    case 'SET_ESTIMATE_REDUCTION_REJECTED': {
      const { error } = action;
      return {
        ...state,
        fetching: false,
        fetched: false,
        error: normaliseError(error),
      };
    }
    default:
      return state;
  }
}

export default reducer;
